import * as React from "react";
import { Control, FieldValues, Path } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";

import { format } from "date-fns";

import {
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_ICONS
} from "components/appointment-status-cell/appointment-status-cell.component";
import AutocompleteFormField from "components/autocomplete-form-field";
import StatusIconContainer from "components/status-transition-cell/status-icon-container.component";
import { APPOINTMENT_STATUSES } from "constants/appointments";
import type { TinyAppointment } from "services/types/appointments.type";

const APPOINTMENT_LABEL_FORMAT = "EEE MMM dd, yyyy hh:mm aaa";

type SelectFittingAppointmentFieldProps<T extends FieldValues> = {
  control: Control<T, object>;
  name: Path<T>;
  appointments: TinyAppointment[];
  isLoading?: boolean;
  disabled?: boolean;
  label?: string;
  onChange?: (appointment: TinyAppointment | null) => void;
};

export default function SelectFittingAppointmentField<T extends FieldValues>({
  control,
  name,
  appointments,
  isLoading = false,
  disabled = false,
  label = "Select Fitting Appointment",
  onChange
}: SelectFittingAppointmentFieldProps<T>) {
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: TinyAppointment) =>
      `${format(new Date(option.startDate), APPOINTMENT_LABEL_FORMAT)} ${
        option.appointmentType?.name ?? ""
      } (${APPOINTMENT_STATUSES.getFromCode(option.status)})`
  });

  return (
    <AutocompleteFormField
      name={name}
      label={label}
      control={control}
      disabled={disabled}
      options={appointments}
      isLoading={isLoading}
      noOptionsText="No fitting appointments found"
      filterOptions={filterOptions}
      onOptionSelected={(option: TinyAppointment | null) =>
        onChange?.(option ?? null)
      }
      getOptionLabel={(option: TinyAppointment) =>
        format(new Date(option.startDate), APPOINTMENT_LABEL_FORMAT)
      }
      isOptionEqualToValue={(option: TinyAppointment, value: TinyAppointment) =>
        option.id === value.id
      }
      renderOption={(props: object, option: TinyAppointment) => (
        <ListItem {...props}>
          <Stack direction="row" gap={1}>
            <StatusIconContainer
              color={APPOINTMENT_STATUS_COLORS[option.status]}
            >
              {APPOINTMENT_STATUS_ICONS[option.status]}
            </StatusIconContainer>
            <ListItemText
              secondary={`${
                option.appointmentType?.name ?? ""
              } (${APPOINTMENT_STATUSES.getFromCode(option.status)})`}
            >
              {format(new Date(option.startDate), APPOINTMENT_LABEL_FORMAT)}
            </ListItemText>
          </Stack>
        </ListItem>
      )}
      popupIcon={<ArrowDropDownIcon />}
    />
  );
}
